import { useEffect, useRef, useState } from "react";
import HeroSection from "./HeroSection";
import HeroCountdown from "./HeroCountdown";

// Wraps multiple hero slides (pills / countdown) with a segmented progress bar
// and play/pause. Only the active slide is mounted, so the pills physics runs
// only while its slide is visible.
const DEFAULT_DURATION = 3; // seconds (PRD: banners auto-rotate every 3s)

export default function HeroCarousel({ props }) {
  const slides = props?.slides || [];
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);

  const current = slides[active];
  const duration = Number(current?.duration) > 0 ? Number(current.duration) : DEFAULT_DURATION;

  const goNext = () => setActive((a) => (slides.length ? (a + 1) % slides.length : 0));
  const goTo = (i) => setActive(i);

  // Auto-advance with a JS timer (kept in sync with the bar animation duration)
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!playing || slides.length <= 1) return;
    timerRef.current = setTimeout(goNext, duration * 1000);
    return () => clearTimeout(timerRef.current);
  }, [active, playing, duration, slides.length]);

  if (slides.length === 0) return null;

  const renderSlide = (slide) =>
    slide?.type === "countdown" ? (
      <HeroCountdown props={slide} />
    ) : (
      <HeroSection props={slide} />
    );

  return (
    <div className="hero-carousel">
      {/* Control bar: play/pause + segmented progress */}
      {slides.length > 1 && (
        <div className="hero-carousel__control">
          <button
            type="button"
            className="hero-carousel__play"
            aria-label={playing ? "Pause" : "Play"}
            onClick={() => setPlaying((p) => !p)}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
                <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path d="M7 5l12 7-12 7z" fill="currentColor" />
              </svg>
            )}
          </button>

          <div className="hero-carousel__bars">
            {slides.map((s, i) => (
              <button
                key={i}
                type="button"
                className="hero-carousel__bar"
                aria-label={`Slide ${i + 1}`}
                onClick={() => goTo(i)}
              >
                <span
                  className={
                    "hero-carousel__fill" +
                    (i < active ? " is-done" : "") +
                    (i === active ? " is-active" : "")
                  }
                  style={
                    i === active
                      ? {
                          animationDuration: duration + "s",
                          animationPlayState: playing ? "running" : "paused",
                        }
                      : undefined
                  }
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active slide (re-keyed so it remounts on change) */}
      <div className="hero-carousel__stage" key={active}>
        {renderSlide(current)}
      </div>
    </div>
  );
}
